/*
 * FRK Voice Search (V1)
 * Browser-native speech recognition -> FRK terminology normalisation -> existing search input.
 * If recognition fails or returns no words, a short message is shown under the search
 * form (styles injected once by this script, so it works in any theme).
 * Does not implement search itself; it only writes text into [data-search-input] and
 * dispatches the same "input" event that typing produces, so predictive-search.js
 * (assets/predictive-search.js) handles everything downstream exactly as it does for typing.
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[FRK Voice Search]';

  var SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;

  // Normalisation dictionary: raw spoken phrase -> canonical FRK term.
  // This is the single place to extend as Search & Discovery / GSC / voice-transcript
  // exports come in later - add entries here, nothing else needs to change.
  var FRK_VOICE_TERMS = [
    { test: /\bcurate\b/gi, replace: 'Cure It' },
    { test: /\bcureit\b/gi, replace: 'Cure It' },
    { test: /\bcure\s+it\b/gi, replace: 'Cure It' },
    { test: /\bflexi\s*tech\b/gi, replace: 'Flexitec' },
    { test: /\bflexi\s*tec\b/gi, replace: 'Flexitec' },
    { test: /\bres\s*tech\b/gi, replace: 'Res-Tec' },
    { test: /\bres\s*tec\b/gi, replace: 'Res-Tec' },
    { test: /\btop\s*coat\b/gi, replace: 'topcoat' },
    { test: /\bfiber\s*glass\b/gi, replace: 'fibreglass' },
    { test: /\bg\s*r\s*p\b/gi, replace: 'GRP' }
  ];

  // V2: assets/frk-voice-lexicon.js (window.FRKVoice) does the clean-up (spoken numbers, units, product codes,
  // brand mishears, catalogue vocabulary) and picks the best of the recogniser's alternatives. The list above is
  // only the fallback if that file did not load. Extra merchant corrections: Theme settings > Search.
  var DEBUG = /[?&]voicedebug=1/.test(location.search) || (function () { try { return localStorage.getItem('frkVoiceDebug') === '1'; } catch (e) { return false; } })();

  function normaliseTranscript(rawTranscript) {
    if (window.FRKVoice) return window.FRKVoice.normalise(rawTranscript, window.FRK_VOICE_EXTRA).text;
    var normalised = rawTranscript.trim();
    FRK_VOICE_TERMS.forEach(function (term) {
      normalised = normalised.replace(term.test, term.replace);
    });
    return normalised;
  }

  // Microsoft Edge sends audio to Windows' online speech service, which only returns
  // text when Windows Settings > Privacy & security > Speech > Online speech
  // recognition is on. Otherwise the mic records but no words ever come back.
  var IS_EDGE = /\bEdg\//.test(navigator.userAgent);
  var LISTEN_TIMEOUT_MS = 10000;

  var MESSAGES = {
    'not-allowed': 'Microphone access is blocked for this site. Allow it using the padlock icon in the address bar, then try again.',
    'service-not-allowed': 'Voice search isn\'t allowed in this browser. Please type your search instead.',
    'audio-capture': 'No microphone was found. Check your microphone is connected, then try again.',
    'no-speech': 'We didn\'t hear anything. Tap the microphone and speak clearly, or type your search.',
    'network': 'Voice search couldn\'t connect to the speech service. Check your connection, or type your search.',
    'no-result': 'Sorry, we didn\'t catch that. Please try again or type your search.',
    'unsupported': 'Voice search isn\'t available right now. Please type your search.'
  };
  var EDGE_HINT = 'Voice search didn\'t return any words. In Microsoft Edge, turn on "Online speech recognition" in Windows Settings > Privacy & security > Speech, or type your search.';

  function messageFor(code) {
    if (IS_EDGE && (code === 'network' || code === 'no-result')) return EDGE_HINT;
    return MESSAGES[code] || MESSAGES.unsupported;
  }

  // Short tones so visitors know when the mic is listening (Web Audio, no sound file).
  // The AudioContext is created inside the mic click so mobile browsers allow playback.
  var audioCtx = null;
  function unlockAudio() {
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtx) audioCtx = new Ctx();
      if (audioCtx.state === 'suspended') audioCtx.resume();
    } catch (e) { audioCtx = null; }
  }
  function playTone(from, to) {
    if (!audioCtx) return;
    try {
      var t = audioCtx.currentTime, osc = audioCtx.createOscillator(), gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(from, t);
      osc.frequency.exponentialRampToValueAtTime(to, t + 0.14);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
      osc.connect(gain); gain.connect(audioCtx.destination);
      osc.start(t); osc.stop(t + 0.2);
    } catch (e) {}
  }

  function injectStyles() {
    if (document.getElementById('frk-voice-msg-styles')) return;
    var style = document.createElement('style');
    style.id = 'frk-voice-msg-styles';
    style.textContent =
      '.frk-voice-msg{display:flex;gap:10px;align-items:flex-start;max-width:640px;margin:10px 0 0;padding:10px 14px;' +
      'border-radius:10px;background:#fff;color:#1d2124;font-size:14px;line-height:1.45;' +
      'box-shadow:0 6px 20px rgba(0,0,0,.14);}' +
      '.frk-voice-msg[hidden]{display:none;}' +
      '.frk-voice-msg__icon{flex:none;display:grid;place-items:center;width:20px;height:20px;border-radius:50%;' +
      'background:#fdecc8;color:#8a5a00;font-size:12px;font-weight:800;}' +
      '.frk-voice-msg__close{flex:none;margin-left:auto;border:0;background:none;color:#55605f;font-size:18px;line-height:1;cursor:pointer;padding:0 2px;}' +
      '.frk-voice-alts{display:flex;flex-wrap:wrap;gap:8px;align-items:center;max-width:640px;margin:10px 0 0;font-size:14px;color:#fff;}' +
      '.frk-voice-alts[hidden]{display:none;}' +
      '.frk-voice-alts__chip{border:1px solid rgba(255,255,255,.55);border-radius:999px;background:rgba(255,255,255,.12);color:#fff;font:inherit;font-weight:700;padding:6px 12px;min-height:34px;cursor:pointer;}' +
      '.frk-voice-alts__chip:hover,.frk-voice-alts__chip:focus-visible{background:#fff;color:#1d2124;outline:0;}' +
      '.frk-voice-debug{max-width:640px;margin:10px 0 0;padding:10px 12px;border-radius:10px;background:#111;color:#cfe;font:12px/1.45 ui-monospace,Menlo,Consolas,monospace;white-space:pre-wrap;word-break:break-word;}' +
      '.frk-voice-debug button{margin:6px 6px 0 0;font:inherit;padding:3px 8px;cursor:pointer;}';
    document.head.appendChild(style);
  }

  function FRKVoiceSearchControl(button) {
    var form = button.closest('form');
    this.button = button;
    this.input = form ? form.querySelector('[data-search-input]') : null;
    this.micIcon = button.querySelector('.frk-search-form__mic-icon--mic');
    this.stopIcon = button.querySelector('.frk-search-form__mic-icon--stop');
    this.recognition = null;
    this.isListening = false;
    this.gotResult = false;
    this.errorShown = false;
    this.watchdog = null;
    this.hideTimer = null;
    this.msg = null;

    if (!this.input) return;

    if (form) {
      injectStyles();
      var msg = document.createElement('div');
      msg.className = 'frk-voice-msg';
      msg.setAttribute('role', 'status');
      msg.setAttribute('aria-live', 'polite');
      msg.hidden = true;
      msg.innerHTML = '<span class="frk-voice-msg__icon" aria-hidden="true">!</span><span class="frk-voice-msg__text"></span>' +
        '<button type="button" class="frk-voice-msg__close" aria-label="Dismiss">&times;</button>';
      // Place the message under the whole search row (form + close button), not inside it.
      var anchor = form;
      var el = form.parentElement;
      for (var depth = 0; el && depth < 3; depth++, el = el.parentElement) {
        var cs = getComputedStyle(el);
        if (cs.display.indexOf('flex') !== -1 && cs.flexDirection.indexOf('row') === 0 && el.children.length > 1) { anchor = el; break; }
      }
      anchor.insertAdjacentElement('afterend', msg);
      msg.querySelector('.frk-voice-msg__close').addEventListener('click', this.hideMessage.bind(this));
      this.msg = msg;
    }

    button.hidden = false;
    button.addEventListener('click', this.onButtonClick.bind(this));
  }

  FRKVoiceSearchControl.prototype.onButtonClick = function () {
    if (this.isListening) {
      this.stop();
    } else {
      this.start();
    }
  };

  FRKVoiceSearchControl.prototype.start = function () {
    var self = this;

    this.hideMessage();
    unlockAudio();
    this.gotResult = false;
    this.errorShown = false;

    var recognition = new SpeechRecognitionCtor();
    recognition.lang = 'en-GB';
    recognition.continuous = false;
    recognition.interimResults = false;
    if ('maxAlternatives' in recognition) recognition.maxAlternatives = 5;
    // Hint the recogniser towards our brand and product words (only where the browser supports it)
    try {
      if (window.FRKVoice && 'phrases' in recognition && typeof window.SpeechRecognitionPhrase === 'function') {
        recognition.phrases = window.FRKVoice.HINTS.map(function (p) { return new window.SpeechRecognitionPhrase(p, 4.0); });
      }
    } catch (e) { /* hints are optional */ }

    recognition.onstart = function () {
      self.isListening = true;
      self.setListeningState(true);
      playTone(660, 990); // rising "listening" tone
      console.log(LOG_PREFIX, 'recognition started');
      clearTimeout(self.watchdog);
      self.watchdog = setTimeout(function () {
        if (self.isListening && !self.gotResult) {
          console.log(LOG_PREFIX, 'no result after ' + LISTEN_TIMEOUT_MS + 'ms, stopping');
          self.stop();
        }
      }, LISTEN_TIMEOUT_MS);
    };

    recognition.onresult = function (event) {
      self.gotResult = true;
      var result = event.results[0];
      var rawTranscript = result[0].transcript;

      var alternatives = [];
      for (var i = 0; i < result.length; i++) {
        alternatives.push({ transcript: result[i].transcript, confidence: result[i].confidence });
      }
      console.log(LOG_PREFIX, 'raw transcript:', rawTranscript);
      console.log(LOG_PREFIX, 'alternatives:', alternatives);

      var normalised = normaliseTranscript(rawTranscript);
      var ranked = null;
      if (window.FRKVoice) {
        ranked = window.FRKVoice.rank(alternatives, window.FRK_VOICE_EXTRA);
        if (ranked.best) normalised = ranked.best.text;
      }
      console.log(LOG_PREFIX, 'normalised transcript:', normalised);

      self.submitToSearch(normalised);
      self.showAlternatives(normalised, ranked);
      if (DEBUG) self.showDebug(rawTranscript, alternatives, ranked, normalised);
    };

    recognition.onerror = function (event) {
      console.log(LOG_PREFIX, 'recognition error:', event.error);
      if (event.error === 'aborted') return;
      self.errorShown = true;
      self.showMessage(messageFor(event.error));
    };

    recognition.onend = function () {
      clearTimeout(self.watchdog);
      if (self.isListening) playTone(880, 520); // falling "stopped" tone
      self.isListening = false;
      self.setListeningState(false);
      console.log(LOG_PREFIX, 'recognition ended');
      // Ended without any words and without an error (typical of Edge when
      // online speech recognition is off): tell the visitor instead of doing nothing.
      if (!self.gotResult && !self.errorShown) self.showMessage(messageFor('no-result'));
    };

    this.recognition = recognition;

    try {
      recognition.start();
    } catch (error) {
      console.log(LOG_PREFIX, 'failed to start recognition:', error);
      self.isListening = false;
      self.setListeningState(false);
      self.errorShown = true;
      self.showMessage(messageFor('unsupported'));
    }
  };

  FRKVoiceSearchControl.prototype.stop = function () {
    if (this.recognition) this.recognition.stop();
  };

  FRKVoiceSearchControl.prototype.showMessage = function (text) {
    if (!this.msg) return;
    var self = this;
    this.msg.querySelector('.frk-voice-msg__text').textContent = text;
    this.msg.hidden = false;
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(function () { self.hideMessage(); }, 12000);
  };

  // "Not what you said?" chips: the other readings of what was heard, tap one to search for it instead
  FRKVoiceSearchControl.prototype.showAlternatives = function (chosen, ranked) {
    var self = this;
    if (!this.msg) return;
    if (!this.alts) {
      this.alts = document.createElement('div');
      this.alts.className = 'frk-voice-alts';
      this.alts.hidden = true;
      this.msg.insertAdjacentElement('afterend', this.alts);
      this.input.addEventListener('keydown', function () { self.alts.hidden = true; });
    }
    var others = ranked ? ranked.others.filter(function (o) { return o.text.toLowerCase() !== chosen.toLowerCase(); }) : [];
    this.alts.textContent = '';
    if (!others.length) { this.alts.hidden = true; return; }
    var label = document.createElement('span');
    label.textContent = 'Not right? Try:';
    this.alts.appendChild(label);
    others.slice(0, 3).forEach(function (o) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'frk-voice-alts__chip';
      b.textContent = o.text;
      b.addEventListener('click', function () { self.submitToSearch(o.text); self.alts.hidden = true; });
      self.alts.appendChild(b);
    });
    this.alts.hidden = false;
  };

  // Test mode (add ?voicedebug=1 to any page): shows what was heard and keeps a log you can copy and send over
  FRKVoiceSearchControl.prototype.showDebug = function (raw, alternatives, ranked, chosen) {
    var entry = {
      t: new Date().toISOString().slice(0, 19),
      heard: raw,
      alternatives: alternatives.map(function (a) { return a.transcript + ' (' + (a.confidence || 0).toFixed(2) + ')'; }),
      searched: chosen,
      fixes: ranked && ranked.best ? ranked.best.changes : []
    };
    var log = [];
    try { log = JSON.parse(localStorage.getItem('frkVoiceLog') || '[]'); } catch (e) { log = []; }
    log.push(entry);
    log = log.slice(-60);
    try { localStorage.setItem('frkVoiceLog', JSON.stringify(log)); } catch (e) { /* private mode */ }
    if (!this.dbg) {
      this.dbg = document.createElement('div');
      this.dbg.className = 'frk-voice-debug';
      (this.alts || this.msg).insertAdjacentElement('afterend', this.dbg);
    }
    var self = this;
    this.dbg.textContent = 'HEARD: ' + raw + '\nALTERNATIVES: ' + entry.alternatives.join(' | ') + '\nSEARCHED: ' + chosen + '\nFIXES: ' + (entry.fixes.join(' ; ') || 'none') + '\n(' + log.length + ' saved)\n';
    var copy = document.createElement('button');
    copy.type = 'button';
    copy.textContent = 'Copy log';
    copy.addEventListener('click', function () {
      var text = JSON.stringify(log, null, 1);
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(function () { copy.textContent = 'Copied'; });
    });
    var clear = document.createElement('button');
    clear.type = 'button';
    clear.textContent = 'Clear log';
    clear.addEventListener('click', function () { try { localStorage.removeItem('frkVoiceLog'); } catch (e) {} self.dbg.textContent = 'Log cleared'; });
    this.dbg.appendChild(copy);
    this.dbg.appendChild(clear);
  };

  FRKVoiceSearchControl.prototype.hideMessage = function () {
    if (!this.msg) return;
    clearTimeout(this.hideTimer);
    this.msg.hidden = true;
  };

  FRKVoiceSearchControl.prototype.setListeningState = function (isListening) {
    this.button.classList.toggle('is-listening', isListening);
    this.button.setAttribute('aria-pressed', isListening ? 'true' : 'false');
    if (this.micIcon) this.micIcon.hidden = isListening;
    if (this.stopIcon) this.stopIcon.hidden = !isListening;
  };

  FRKVoiceSearchControl.prototype.submitToSearch = function (finalQuery) {
    if (!finalQuery) return;

    console.log(LOG_PREFIX, 'final query injected into search:', finalQuery);

    this.input.value = finalQuery;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  function init() {
    var buttons = document.querySelectorAll('[data-voice-search-button]');
    if (!buttons.length) return;

    if (!SpeechRecognitionCtor) {
      console.log(LOG_PREFIX, 'speech recognition not supported in this browser, mic stays hidden');
      return;
    }

    buttons.forEach(function (button) {
      new FRKVoiceSearchControl(button);
    });
  }

  init();
})();
